import { useCallback } from 'react';

interface UseFormKeyboardShortcutsOptions {
  onSubmit?: () => void;
  onCancel?: () => void;
  /** Whether form is currently submitting (disables shortcuts) */
  isLoading?: boolean;
}

/**
 * Hook for consistent form keyboard shortcuts:
 * - Enter on single-line inputs → submit
 * - Escape → cancel/close
 * - Ctrl/Cmd+Enter → submit (works from textareas too)
 * - Textarea Enter → newline (preserved)
 */
export function useFormKeyboardShortcuts({
  onSubmit,
  onCancel,
  isLoading = false,
}: UseFormKeyboardShortcutsOptions) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isLoading) return;

      // A nested control (an open Select/Popover/menu, a Radix dismissable
      // layer, etc.) that already handled this key marks the native event
      // defaultPrevented. Don't also hijack it for form submit/cancel —
      // otherwise Escape dismissing a dropdown would also close the whole
      // dialog. defaultPrevented lives on the shared native event, so it is
      // reliable even across the separate cross-portal React dispatch paths
      // where stopPropagation can't reach.
      if (e.defaultPrevented) return;

      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      const isInput = tagName === 'input';
      const inputType = isInput ? (target as HTMLInputElement).type : '';
      // Text-like inputs that should submit on Enter (textareas are excluded - they use Enter for newlines)
      const isTextInput =
        isInput &&
        ['text', 'email', 'password', 'search', 'tel', 'url', 'number'].includes(inputType);

      // Escape always cancels
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
        return;
      }

      // Ctrl/Cmd+Enter always submits (power user shortcut, especially for textareas)
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        onSubmit?.();
        return;
      }

      // Enter submits from text inputs (not textareas, not buttons, not selects)
      if (e.key === 'Enter' && isTextInput && !e.shiftKey) {
        e.preventDefault();
        onSubmit?.();
      }
    },
    [onSubmit, onCancel, isLoading]
  );

  return { handleKeyDown };
}
